/**
 * RoleAttributesSection - define custom attributes on a role.
 *
 * Ported from the member custom-attributes feature into a per-role structure:
 * a role (e.g. Resolution Specialist) declares attributes (e.g. geozone) here,
 * and every holder of the role then sets a value for them on their member
 * profile (Settings -> Team). Backed by role-scoped ``MembershipAttribute``
 * definitions (``applies_to_role`` set) via the ``/member-attributes/`` API.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { TFunction } from 'i18next'
import { toastApiError } from '@/shared/lib/api-error'
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog'
import { AttributeConfigurationField } from '@/admin/components/shared/attribute-configuration-field'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import {
  isChoiceAttributeFieldType,
  optionValuesFromConfig,
  optionsToPayload,
  type AttributeOption,
} from '@/admin/components/shared/attribute-configuration-utils'
import {
  useCreateRoleAttribute,
  useDeleteRoleAttribute,
  useRoleAttributes,
  useUpdateRoleAttribute,
  type RoleAttribute,
} from '@/admin/hooks/use-role-attributes'

// Field types offered for a role attribute. Labels are resolved through `t`
// so they follow the i18n catalogue like the rest of the copy.
// Each type carries a descriptive label (for the Type dropdown, where there is
// room) and a short label (for the compact badge in the attribute list row).
const FIELD_TYPE_DEFS: {
  value: string
  key: string
  fallback: string
  shortKey: string
  shortFallback: string
}[] = [
  {
    value: 'TEXT',
    key: 'roles.attr.type.text',
    fallback: 'Text',
    shortKey: 'roles.attr.typeShort.text',
    shortFallback: 'Text',
  },
  {
    value: 'NUMBER',
    key: 'roles.attr.type.number',
    fallback: 'Number',
    shortKey: 'roles.attr.typeShort.number',
    shortFallback: 'Number',
  },
  {
    value: 'SELECT',
    key: 'roles.attr.type.select',
    fallback: 'Select (one of a list)',
    shortKey: 'roles.attr.typeShort.select',
    shortFallback: 'Select',
  },
  {
    value: 'MULTI_SELECT',
    key: 'roles.attr.type.multiSelect',
    fallback: 'Multi-select (several of a list)',
    shortKey: 'roles.attr.typeShort.multiSelect',
    shortFallback: 'Multi-select',
  },
  {
    value: 'BOOLEAN',
    key: 'roles.attr.type.boolean',
    fallback: 'Yes / No',
    shortKey: 'roles.attr.typeShort.boolean',
    shortFallback: 'Yes / No',
  },
  {
    value: 'EMAIL',
    key: 'roles.attr.type.email',
    fallback: 'Email',
    shortKey: 'roles.attr.typeShort.email',
    shortFallback: 'Email',
  },
  {
    value: 'PHONE',
    key: 'roles.attr.type.phone',
    fallback: 'Phone',
    shortKey: 'roles.attr.typeShort.phone',
    shortFallback: 'Phone',
  },
  {
    value: 'DATE',
    key: 'roles.attr.type.date',
    fallback: 'Date',
    shortKey: 'roles.attr.typeShort.date',
    shortFallback: 'Date',
  },
]

function fieldTypeOptions(t: TFunction): { value: string; label: string }[] {
  return FIELD_TYPE_DEFS.map((f) => ({ value: f.value, label: t(f.key, f.fallback) }))
}

function fieldTypeShort(t: TFunction, value: string): string {
  const def = FIELD_TYPE_DEFS.find((f) => f.value === value)
  return def ? t(def.shortKey, def.shortFallback) : value
}

interface Props {
  roleUuid: string
  roleName: string
}

export function RoleAttributesSection({ roleUuid, roleName }: Props) {
  const { t } = useTranslation()
  const { data: attributes, isPending } = useRoleAttributes(roleUuid)
  const deleteAttr = useDeleteRoleAttribute(roleUuid)
  const createAttr = useCreateRoleAttribute(roleUuid)
  const resourceLookupProviders = useResourceLookupProviders()

  const [editing, setEditing] = useState<RoleAttribute | null>(null)
  const [creating, setCreating] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const [deleting, setDeleting] = useState<RoleAttribute | null>(null)

  const rows = attributes ?? []

  // "Add attribute" opens the shared attribute-bucket popover (same UX as the
  // Entity Editor / template pages). Picking an existing shared attribute
  // attaches it to this role directly; typing a new name opens the create
  // dialog prefilled with that name.
  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setEditing(null)
      setCreating(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await createAttr.mutateAsync({
          code_name: tpl.code_name,
          display_name: tpl.display_name,
          field_type: tpl.field_type,
          configuration_data: tpl.configuration_data ?? {},
          required: false,
        })
        toast.success(t('roles.attr.added', 'Attribute added.'))
      } catch (err) {
        toastApiError(err, t('roles.attr.saveError', 'Could not save the attribute.'))
      }
    }
  }

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
      <section className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {t('roles.attr.sectionTitle', 'Attributes')}
            </h3>
            <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink-3)]">
              {t(
                'roles.attr.sectionHint',
                'Fields every {{role}} member fills in on their profile.',
                { role: roleName || t('roles.attr.roleFallback', 'role') },
              )}
            </p>
          </div>
          {/* The whole tenant attribute bucket (no domain filter) so a role can
              reuse any shared attribute definition, matching the Add-attribute
              UX on the Entity Editor / template pages. */}
          <AttributeTemplatePicker
            onPick={handleAddAttrPick}
            resetAfterPick
            asAddButton
            addButtonLabel={t('roles.attr.add', 'Add attribute')}
            data-testid="role-attribute-add"
          />
        </div>

      {isPending ? (
        <div className="flex items-center gap-2 py-5 text-[12px] text-[var(--ink-3)]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('roles.attr.loading', 'Loading attributes')}
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] px-4 py-5 text-center text-[12px] leading-relaxed text-[var(--ink-3)]">
          {t(
            'roles.attr.empty',
            'No attributes yet. Add one to collect it from every member with this role.',
          )}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((attr) => (
            <li
              key={attr.uuid}
              data-testid={`role-attribute-row-${attr.code_name}`}
              className="group flex items-center justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-3.5 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] font-medium text-[var(--ink)]">
                    {attr.display_name}
                  </span>
                  {attr.required && (
                    <Badge
                      variant="secondary"
                      className="shrink-0 text-[10px] uppercase tracking-[0.08em]"
                    >
                      {t('roles.attr.required', 'Required')}
                    </Badge>
                  )}
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="truncate font-mono text-[11px] text-[var(--ink-3)]">
                    {attr.code_name}
                  </code>
                  <Badge
                    variant="outline"
                    className="shrink-0 whitespace-nowrap text-[10px] font-normal text-[var(--ink-3)]"
                  >
                    {fieldTypeShort(t, attr.field_type)}
                  </Badge>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1 text-[var(--ink-3)] opacity-70 transition-opacity group-hover:opacity-100">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('roles.attr.editAria', 'Edit {{name}}', {
                    name: attr.display_name,
                  })}
                  onClick={() => setEditing(attr)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('roles.attr.deleteAria', 'Delete {{name}}', {
                    name: attr.display_name,
                  })}
                  onClick={() => setDeleting(attr)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

        {(creating || editing) && (
          <AttributeDefinitionDialog
            roleUuid={roleUuid}
            attribute={editing}
            prefillName={prefillName}
            onClose={() => {
              setCreating(false)
              setEditing(null)
              setPrefillName('')
            }}
          />
        )}

        <AttributeDeleteDialog
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          attributeName={deleting?.display_name}
          description={t(
            'roles.attr.deleteBody',
            'Member values already set for it are kept but no longer shown.',
          )}
          successMessage={t('roles.attr.deleted', 'Attribute deleted.')}
          errorFallback={t(
            'roles.attr.deleteError',
            'Could not delete the attribute.',
          )}
          isPending={deleteAttr.isPending}
          onDelete={async () => {
            if (!deleting) return
            await deleteAttr.mutateAsync(deleting.code_name)
            setDeleting(null)
          }}
        />
      </section>
    </ResourceLookupContextProvider>
  )
}

const FORM_ID = 'role-attribute-definition-form'

interface DialogProps {
  roleUuid: string
  attribute: RoleAttribute | null
  prefillName?: string
  onClose: () => void
}

function AttributeDefinitionDialog({
  roleUuid,
  attribute,
  prefillName = '',
  onClose,
}: DialogProps) {
  const { t } = useTranslation()
  const isEdit = !!attribute
  const createAttr = useCreateRoleAttribute(roleUuid)
  const updateAttr = useUpdateRoleAttribute(roleUuid)
  const saving = createAttr.isPending || updateAttr.isPending

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: t('roles.attr.labelField', 'Label'),
        placeholder: t('roles.attr.labelPlaceholder', 'e.g. Geozone'),
        value: attribute?.display_name ?? prefillName,
        autoFocus: true,
        validationSchema: z
          .string()
          .min(1, t('roles.attr.labelRequired', 'Label is required')),
      },
      {
        type: 'select',
        name: 'field_type',
        label: t('roles.attr.typeField', 'Type'),
        value: attribute?.field_type ?? 'TEXT',
        options: fieldTypeOptions(t),
      },
      {
        // Reuse the Entity Editor's shared options/default-value editor so the
        // add-attribute UX matches Product / Transaction templates (tag-based
        // options, per-type default). Only shown for choice types.
        type: 'custom',
        name: 'option_values',
        label: t('roles.attr.optionsField', 'Options'),
        value: optionValuesFromConfig(attribute?.configuration_data?.options),
        visible: (ctx) =>
          isChoiceAttributeFieldType(String(ctx.values.field_type ?? '')),
        render: (ctx) => (
          <AttributeConfigurationField
            fieldType={String(ctx.values.field_type ?? '')}
            options={(ctx.values.option_values ?? []) as AttributeOption[]}
            onOptionsChange={(next) => ctx.setFieldValue('option_values', next)}
            defaultValue={
              (ctx.values.default_value ?? null) as
                | string
                | number
                | boolean
                | string[]
                | null
            }
            onDefaultValueChange={(next) => ctx.setFieldValue('default_value', next)}
          />
        ),
      },
      {
        type: 'checkbox',
        name: 'required',
        label: t('roles.attr.requiredField', 'Required'),
        value: attribute?.required ?? false,
      },
    ],
    [attribute, prefillName, t],
  )

  const { Form } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (raw) => {
      const values = raw as Record<string, unknown>
      const fieldType = String(values.field_type ?? 'TEXT')
      const isChoice = isChoiceAttributeFieldType(fieldType)
      const options = isChoice
        ? optionsToPayload((values.option_values ?? []) as AttributeOption[])
        : []
      const defaultValue = values.default_value ?? null
      const configuration_data: Record<string, unknown> = {}
      if (isChoice) configuration_data.options = options
      if (
        defaultValue !== null &&
        defaultValue !== '' &&
        !(Array.isArray(defaultValue) && defaultValue.length === 0)
      ) {
        configuration_data.default_value = defaultValue
      }

      // A Select / Multi-select with no options renders a dead dropdown, so
      // require at least one before saving.
      if (isChoice && options.length === 0) {
        toast.error(
          t(
            'roles.attr.optionsRequired',
            'Add at least one option for a Select or Multi-select field.',
          ),
        )
        return
      }

      try {
        if (isEdit && attribute) {
          await updateAttr.mutateAsync({
            codeName: attribute.code_name,
            input: {
              display_name: String(values.display_name ?? '').trim(),
              field_type: fieldType,
              configuration_data,
              required: Boolean(values.required),
            },
          })
          toast.success(t('roles.attr.updated', 'Attribute updated.'))
        } else {
          const displayName = String(values.display_name ?? '').trim()
          // The code name is an internal key, not something an admin should
          // hand-author - derive a valid slug from the label
          // (^[a-z][a-z0-9_]*$, matching the backend RegexField).
          const codeName =
            displayName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '_')
              .replace(/^_+|_+$/g, '')
              .replace(/^([0-9])/, 'f_$1') || 'field'
          await createAttr.mutateAsync({
            code_name: codeName,
            display_name: displayName,
            field_type: fieldType,
            configuration_data,
            required: Boolean(values.required),
          })
          toast.success(t('roles.attr.added', 'Attribute added.'))
        }
        onClose()
      } catch (err: unknown) {
        const detail =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { data?: { result?: unknown } } }).response
                ?.data?.result
            : undefined
        const codeErr =
          detail && typeof detail === 'object' && 'code_name' in detail
            ? (detail as { code_name?: string[] }).code_name?.[0]
            : undefined
        toast.error(
          codeErr ?? t('roles.attr.saveError', 'Could not save the attribute.'),
        )
      }
    },
    [fields, isEdit, attribute, createAttr, updateAttr, t],
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="role-attribute-definition-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] text-[var(--ink)]">
            {isEdit
              ? t('roles.attr.editTitle', 'Edit attribute')
              : t('roles.attr.add', 'Add attribute')}
          </DialogTitle>
        </DialogHeader>

        {/*
          Footer lives INSIDE <Form> (rendered as its children, inside the
          <form> element) so the submit button submits natively. An external
          `form={id}` button does not reliably submit here - this dialog is
          nested inside the role-edit dialog, and the cross-portal association
          silently no-ops (the create POST never fired).
        */}
        <Form className="space-y-4">
          <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="role-attribute-definition-submit"
            >
              {saving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {isEdit
                ? t('common.saveChanges', 'Save changes')
                : t('roles.attr.add', 'Add attribute')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
